import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import DataTable from '@/components/DataTable.jsx';
import { api, ApiError } from '@/lib/api.js';
import './ArchivePage.css';

const num = (key, label, title) => ({ key, label, title: title ?? label, numeric: true });
const nf = new Intl.NumberFormat('en-GB');

/*
 * Completed seasons, league by league.
 *
 * Season and league live in the path — /archive/2024-25/allstars — so a
 * standings table can be linked to directly, as the AngularJS route already
 * allowed. Picking either one navigates rather than mutating hidden state.
 *
 * Captain, hits and ITG columns only appear for seasons where those were
 * recorded, matching the old allColumns flag.
 */
const columns = (showAll) =>
	[
		{ key: 'position', label: 'Pos', title: 'Finishing position', numeric: true, defaultSort: 'asc' },
		{
			key: 'name',
			label: 'Manager',
			strong: true,
			render: (name, row) => (
				<Link to={`/archive/${row.season}/manager/${row.manager_code}`}>{name}</Link>
			),
		},
		{ ...num('final_overall_rank', 'Overall rank'), render: (v) => nf.format(v ?? 0) },
		{ ...num('total_points', 'Total pts', 'Total points') },
		{ ...num('mean_rank', 'Mean GW rank'), render: (v) => nf.format(v ?? 0) },
		{ ...num('median_rank', 'Median GW rank'), render: (v) => nf.format(v ?? 0) },
		showAll && {
			key: 'captain_total',
			label: 'Captain pts',
			title: 'Captain points',
			numeric: true,
		},
		num('bench_points', 'Bench', 'Points left on the bench'),
		num('num_tx', 'Transfers'),
		showAll && num('hits', 'Hits', 'Points spent on transfers'),
		showAll && num('season_itg', 'ITG', 'Immediate transfer gain'),
		{ ...num('final_tv', 'Value', 'Final team value'), render: (v) => `£${v}` },
	].filter(Boolean);

export default function ArchivePage() {
	const { season: seasonParam, league: leagueParam } = useParams();
	const navigate = useNavigate();

	const [seasons, setSeasons] = useState([]);
	const [leagues, setLeagues] = useState([]);
	const [rows, setRows] = useState(null);
	const [status, setStatus] = useState('idle');
	const [error, setError] = useState(null);

	useEffect(() => {
		api
			.post('/api/archive', {})
			.then((d) => setSeasons(d?.seasons ? [...d.seasons].sort().reverse() : []))
			.catch(() => setSeasons([]));
	}, []);

	/* Leagues depend on the season, so they reload whenever it changes. */
	useEffect(() => {
		if (!seasonParam) {
			setLeagues([]);
			return;
		}
		api
			.post('/api/archive', { season: seasonParam })
			.then((d) => setLeagues(d?.leagues ?? []))
			.catch(() => setLeagues([]));
	}, [seasonParam]);

	useEffect(() => {
		if (!seasonParam || !leagueParam) {
			setRows(null);
			setStatus('idle');
			return undefined;
		}
		let cancelled = false;
		setStatus('loading');
		setError(null);
		api
			.post('/api/archive', { season: seasonParam, league: leagueParam })
			.then((data) => {
				if (cancelled) return;
				if (!Array.isArray(data) || !data.length) {
					setError(`No standings recorded for ${leagueParam} in ${seasonParam}.`);
					setStatus('error');
					return;
				}
				/* captain_stats is nested; flatten the one field the table shows. */
				setRows(
					data.map((row) => ({ ...row, captain_total: row.captain_stats?.total })),
				);
				setStatus('ready');
			})
			.catch((err) => {
				if (cancelled) return;
				setError(err instanceof ApiError ? err.message : 'Could not load that league.');
				setStatus('error');
			});
		return () => {
			cancelled = true;
		};
	}, [seasonParam, leagueParam]);

	const showAll = Boolean(rows?.[0]?.captain_stats);

	return (
		<div className="archive-page">
			<header className="archive-page__intro">
				<h1>League archive</h1>
				<p className="archive-page__lede">
					Final standings for completed seasons. Pick a manager to see their
					season in full.
				</p>
			</header>

			<div className="archive-picker">
				<label className="archive-picker__field">
					<span className="field__label">Season</span>
					<select
						className="field__input"
						value={seasonParam ?? ''}
						onChange={(e) => navigate(`/archive/${e.target.value}`)}
					>
						<option value="">Choose a season</option>
						{seasons.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</label>

				<label className="archive-picker__field">
					<span className="field__label">League</span>
					<select
						className="field__input"
						value={leagueParam ?? ''}
						disabled={!leagues.length}
						onChange={(e) => navigate(`/archive/${seasonParam}/${e.target.value}`)}
					>
						<option value="">
							{seasonParam ? 'Choose a league' : 'Pick a season first'}
						</option>
						{leagues.map((l) => (
							<option key={l} value={l}>
								{l}
							</option>
						))}
					</select>
				</label>
			</div>

			{status === 'loading' && (
				<div className="tab-loading" role="status">
					<span className="spinner" aria-hidden="true" />
					<span>Loading {leagueParam}…</span>
				</div>
			)}

			{status === 'error' && (
				<div className="notice notice--error" role="alert">
					<strong>Could not load that league.</strong>
					<span>{error}</span>
				</div>
			)}

			{status === 'ready' && (
				<section>
					<h2 className="section-title">
						{leagueParam} · {seasonParam}
					</h2>
					<DataTable
						columns={columns(showAll)}
						rows={rows}
						rowKey={(r) => r.manager_code}
						filterPlaceholder="Filter by manager"
						maxHeight="70vh"
					/>
				</section>
			)}
		</div>
	);
}
